import React, { useState, useMemo } from "react"
import Typography from "aldoo-ra/Typography"
import Badge from "aldoo-ra/Badge"
import Avatar from "aldoo-ra/Avatar"
import Input from "aldoo-ra/Input"
import moment from "moment"
import { ChevronRightIcon } from "@heroicons/react/24/outline"

/**
 * Get action badge variant based on action type
 */
const getActionVariant = (action) => {
  switch (action) {
    case 'create':
      return "admin_success"
    case 'update':
      return "admin_info"
    case 'delete':
      return "admin_error"
    case 'soft-delete':
      return "admin_warning"
    default:
      return "admin"
  }
}

/**
 * Process fields to group array modifications and strip CMS prefix
 */
const processFields = (fields) => {
  if (!fields || fields.length === 0) return []

  const groups = {}
  const standalone = []

  fields.forEach(field => {
    // Strip "content." prefix for CMS models
    let displayField = field
    if (field.startsWith('content.')) {
      displayField = field.substring(8) // Remove "content."
    }

    // Check if it's an array modification (e.g., "features.0.content")
    const arrayMatch = displayField.match(/^([^.]+)\.(\d+)\.(.+)$/)

    if (arrayMatch) {
      const [, arrayName, index, subField] = arrayMatch
      const groupKey = `${arrayName}.${index}`

      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          displayName: `${arrayName}[${index}]`,
          originalField: field,
          subFields: []
        }
      }
      groups[groupKey].subFields.push({ field: subField, originalField: field })
    } else {
      standalone.push({ field: displayField, originalField: field })
    }
  })

  return {
    groups: Object.values(groups),
    standalone
  }
}

/**
 * History Details Panel - Shows detailed information about a history entry
 */
const HistoryDetailsPanel = ({ entry, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [expandedGroups, setExpandedGroups] = useState({})

  if (!entry) return null

  // Process and filter fields
  const { filteredGroups, filteredStandalone, filteredCount } = useMemo(() => {
    if (!entry.changes || entry.changes.length === 0) {
      return { filteredGroups: [], filteredStandalone: [], filteredCount: 0 }
    }

    const { groups, standalone } = processFields(entry.changes)

    if (!searchTerm.trim()) {
      return {
        filteredGroups: groups,
        filteredStandalone: standalone,
        filteredCount: entry.changes.length
      }
    }

    const lowerSearch = searchTerm.toLowerCase()

    // Filter standalone fields
    const filteredStandalone = standalone.filter(item =>
      item.field.toLowerCase().includes(lowerSearch)
    )

    // Filter groups - include if group name or any subfield matches
    const filteredGroups = groups.map(group => {
      const groupMatches = group.displayName.toLowerCase().includes(lowerSearch)
      const matchingSubFields = group.subFields.filter(sub =>
        sub.field.toLowerCase().includes(lowerSearch)
      )

      if (groupMatches || matchingSubFields.length > 0) {
        return {
          ...group,
          subFields: groupMatches ? group.subFields : matchingSubFields
        }
      }
      return null
    }).filter(Boolean)

    // Calculate filtered count
    const filteredCount = filteredStandalone.length +
      filteredGroups.reduce((sum, group) => sum + group.subFields.length, 0)

    return { filteredGroups, filteredStandalone, filteredCount }
  }, [entry.changes, searchTerm])

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  return (
    <div className="p-6 pb-12">
        {/* Basic Info */}
        <div className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Avatar
                user={{ name: entry.author || "Unknown" }}
                variant="admin_medium"
              />

              <div className="flex flex-col">
                <Typography 
                  variant="admin_label" 
                  className="my-0"
                >
                  {entry.author || "Unknown"}
                </Typography>

                {entry.user?.email && (
                  <Typography variant="admin_txt1_dim">
                    {entry.user.email}
                  </Typography>
                )}
              </div>
            </div>

            <Typography 
              variant="admin_txt1_dim" 
              className="text-right"
            >
              {moment(entry.timestamp).format('MMMM D, YYYY [at] h:mm A')}
            </Typography>
          </div>

          <div className="flex flex-col items-center gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Badge 
                variant={getActionVariant(entry.action)} className="capitalize"
              >
                {entry.action}
              </Badge>

              {entry.context?.bulkOperation && (
                <Badge variant="admin_info">
                  Bulk Operation
                </Badge>
              )}
            </div>
            
            <Typography 
              variant="admin_txt1" 
              className="text-admin_text"
            >
              {entry.targetModel}
            </Typography>

            {entry.targetName && (
              <Typography 
                variant="admin_txt1" 
                className="text-admin_text"
              >
                <span className="text-admin_text_secondary">
                  Target:
                </span> {entry.targetName}
              </Typography>
            )}
          </div>
        </div>

        {/* Changelog for Bulk Operations */}
        {entry.changelog && (
          <div className="mb-6 border-l-4 border-admin_primary pl-4">
            <Typography 
              variant="admin_label" 
              className="flex items-center gap-2"
            >
              Summary
              {entry.context?.itemCount && (
                <Badge 
                  variant="admin_info" 
                  className="text-xs"
                >
                  {entry.context.itemCount} items
                </Badge>
              )}
            </Typography>

            <div className="bg-admin_canvas rounded p-3">
              <Typography 
                variant="admin_txt1" 
                className="text-admin_text"
              >
                {typeof entry.changelog === 'string'
                  ? entry.changelog
                  : JSON.stringify(entry.changelog, null, 2)}
              </Typography>
            </div>
          </div>
        )}

        {/* Changed Fields (for non-bulk operations) */}
        {!entry.changelog && entry.changes && entry.changes.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <Typography 
                variant="admin_label" 
                className="mb-0"
              >
                Changed Fields
              </Typography>

              <Badge variant="admin_info">
                {searchTerm.trim() && filteredCount !== entry.changes.length
                  ? `${filteredCount} / ${entry.changes.length} ${entry.changes.length === 1 ? 'change' : 'changes'}`
                  : `${entry.changes.length} ${entry.changes.length === 1 ? 'change' : 'changes'}`
                }
              </Badge>
            </div>

            {/* Search Input */}
            <div className="mb-3">
              <Input
                variant="admin"
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                showClearButton={true}
                clearButtonVariant="icon_only"
              />
            </div>

            {/* Fields List with limited height */}
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-transparent">
              {/* Standalone Fields */}
              {filteredStandalone.map((item, i) => (
                <div key={`standalone-${i}`} className="font-mono text-sm text-admin_text bg-admin_canvas rounded px-3 py-2">
                  {item.field}
                </div>
              ))}

              {/* Grouped Array Fields */}
              {filteredGroups.map((group) => (
                <div key={group.key} className="bg-admin_canvas rounded overflow-hidden">
                  {/* Group Header - Clickable */}
                  <div
                    className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-admin_neutral_1 transition-colors"
                    onClick={() => toggleGroup(group.key)}
                  >
                    <ChevronRightIcon
                      className={`w-4 h-4 text-admin_text_secondary transition-transform ${
                        expandedGroups[group.key] ? 'rotate-90' : ''
                      }`}
                    />

                    <span className="font-mono text-sm text-admin_text font-medium">
                      {group.displayName}
                    </span>

                    <Badge 
                      variant="admin_info" 
                      className="ml-auto"
                    >
                      {group.subFields.length} {group.subFields.length === 1 ? 'field' : 'fields'}
                    </Badge>
                  </div>

                  {/* Sub-fields - Expandable */}
                  {expandedGroups[group.key] && (
                    <div className="pl-8 pr-3 pb-2 space-y-1">
                      {group.subFields.map((sub, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 bg-admin_paper rounded font-mono text-xs text-admin_text_secondary"
                        >
                          {sub.field}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* No Results */}
              {filteredStandalone.length === 0 && filteredGroups.length === 0 && (
                <div className="px-3 py-4 text-center text-admin_text_secondary text-sm">
                  No fields match your search
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tags */}
        {entry.tags && entry.tags.length > 0 && (
          <div className="mb-6">
            <Typography variant="admin_label">
              Tags
            </Typography>

            <div className="flex flex-wrap gap-2">
              {entry.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-admin_neutral_1 text-admin_text rounded-full text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Context */}
        {entry.context && (
          <div className="mb-6">
            <Typography variant="admin_label">
              Context
            </Typography>

            <div className="space-y-2 text-sm">
              {entry.context.bulkOperation && (
                <div className="flex justify-between">
                  <span className="text-admin_text_secondary">
                    Operation Type:
                  </span>

                  <span className="text-admin_text">
                    Bulk Operation
                  </span>
                </div>
              )}

              {entry.context.itemCount && (
                <div className="flex justify-between">
                  <span className="text-admin_text_secondary">
                    Items Affected:
                  </span>

                  <span className="text-admin_text">
                    {entry.context.itemCount}
                  </span>
                </div>
              )}

              {entry.context.ip && (
                <div className="flex justify-between">
                  <span className="text-admin_text_secondary">
                    IP Address:
                  </span>

                  <span className="font-mono text-admin_text">
                    {entry.context.ip}
                  </span>
                </div>
              )}

              {entry.context.deviceID && (
                <div className="flex justify-between">
                  <span className="text-admin_text_secondary">
                    Device ID:
                  </span>

                  <span className="font-mono text-admin_text">
                    {entry.context.deviceID}
                  </span>
                </div>
              )}

              {entry.context.endpoint && (
                <div className="flex justify-between">
                  <span className="text-admin_text_secondary">
                    Endpoint:
                  </span>

                  <span className="font-mono text-admin_text">
                    {entry.context.endpoint}
                  </span>
                </div>
              )}

              {entry.context.userAgent && (
                <div className="flex justify-between">
                  <span className="text-admin_text_secondary">
                    User Agent:
                  </span>

                  <p className="text-admin_text break-all mt-1">
                    {entry.context.userAgent}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  )
}

export default HistoryDetailsPanel
